'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { Button } from '@/components/ui/button'
import { Container } from '@/components/ui/container'
import { Measure } from '@/components/ui/measure'
import { Section } from '@/components/ui/section'
import { TypographyH1, TypographyP, TypographySpan } from '@/components/ui/typography'
import { pathWithLocale } from '@/lib/i18n/locale-path'
import { paths } from '@/lib/routes/site-paths'
import { normalizeLocale, type AppLocale } from '@/lib/i18n/settings'
import { Col } from '@/components/ui/col'
import { GradientOverlay } from '@/components/ui/gradient-overlay'

function getLocaleFromPath(pathname: string) {
  const seg = pathname.split('/').filter(Boolean)[0] ?? 'en'
  return normalizeLocale(seg)
}

const COPY = {
  vi: {
    code: '500' as const,
    title: 'Đã xảy ra lỗi ngoài ý muốn.',
    description: 'Hệ thống vừa gặp lỗi khi xử lý yêu cầu của bạn. Bạn có thể thử lại hoặc quay về trang chủ.',
    retry: 'Thử lại',
    home: 'Về Trang chủ',
    contact: 'Liên hệ hỗ trợ',
  },
  en: {
    code: '500' as const,
    title: 'Something went wrong unexpectedly.',
    description: 'The system encountered an error while handling your request. You can retry or return to home.',
    retry: 'Try Again',
    home: 'Back to Home',
    contact: 'Contact Support',
  },
  ja: {
    code: '500' as const,
    title: '予期しないエラーが発生しました。',
    description:
      'リクエストの処理中にシステムエラーが発生しました。再試行するか、ホームに戻ってください。',
    retry: '再試行',
    home: 'ホームへ戻る',
    contact: 'サポートに問い合わせる',
  },
  zh: {
    code: '500' as const,
    title: '出现意外错误。',
    description: '在处理请求时，系统遇到了错误。你可以再次尝试或返回主页。',
    retry: '再试一次',
    home: '返回主页',
    contact: '联系支持',
  },
  ko: {
    code: '500' as const,
    title: '예기치 않은 오류가 발생했습니다.',
    description:
      '요청을 처리하는 동안 시스템 오류가 발생했습니다. 다시 시도하거나 홈페이지로 돌아가세요.',
    retry: '다시 시도',
    home: '홈페이지로 돌아가기',
    contact: '지원팀 문의',
  },
} satisfies Record<AppLocale, { code: '500'; title: string; description: string; retry: string; home: string; contact: string }>

export default function SiteErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const pathname = usePathname() ?? '/en'
  const locale = getLocaleFromPath(pathname)
  const text = COPY[locale]

  console.error(error)

  return (
    <Section tone="white" border="bGray200" className="relative py-[120px]">
      <GradientOverlay className="bg-grid-pattern opacity-40" />
      <Container size="site" className="relative z-10">
        <Measure className="text-center">
          <TypographySpan variant="eyebrow" tone="brand" className="mb-4 block text-fs-sm">
            {text.code}
          </TypographySpan>
          <TypographyH1 className="mb-6 md:text-5xl">{text.title}</TypographyH1>
          <Measure size="2xl" className="mb-10">
            <TypographyP tone="subdued" size="lead" className="leading-relaxed">
              {text.description}
            </TypographyP>
          </Measure>

          <Col className="justify-center gap-4 sm:flex-row">
            <Button variant="primary" size="md" onClick={reset}>
              {text.retry}
            </Button>
            <Button asChild variant="secondaryLight" size="md">
              <Link href={`/${locale}`}>{text.home}</Link>
            </Button>
            <Button asChild variant="secondaryLight" size="md">
              <Link href={pathWithLocale(locale, paths.contact)}>{text.contact}</Link>
            </Button>
          </Col>
        </Measure>
      </Container>
    </Section>
  )
}
